import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { conns } from '@cfg/axiosConfig.tsx';
import type { RootState } from '@store/store';
import { ImageMinimumInfo } from '@app-types/commons/images.types.ts';
import { isAxiosError } from 'axios';

export interface ShippingMethodApiError {
  code?: string;
  message: string;
  status?: number;
}

const getShippingMethodApiError = (
  error: unknown,
  fallback: string,
): ShippingMethodApiError => {
  if (isAxiosError(error)) {
    const response = error.response;
    const data = response?.data as { detail?: unknown; message?: unknown } | undefined;
    const detail = data?.detail;
    if (detail && typeof detail === 'object') {
      const structured = detail as { code?: unknown; message?: unknown };
      if (typeof structured.message === 'string' && structured.message.trim()) {
        return {
          code: typeof structured.code === 'string' ? structured.code : undefined,
          message: structured.message,
          status: response?.status,
        };
      }
    }
    if (typeof detail === 'string' && detail.trim()) {
      return { message: detail, status: response?.status };
    }
    if (typeof data?.message === 'string' && data.message.trim()) {
      return { message: data.message, status: response?.status };
    }
    if (error.message?.trim()) {
      return { message: error.message, status: response?.status };
    }
  }
  return { message: fallback };
};

export interface ShippingMethod {
  id: number;
  name: string;
  code: string;
  base_cost?: number | null;
  strategy: string;
  config?: Record<string, unknown> | null;
  image_id?: number | null;
  image?: ImageMinimumInfo;
  coverage?: CoverageArea[];
  is_active: boolean;
  is_deleted?: boolean;
  payment_policy_mode?: 'default' | 'custom';
  accepted_payment_method_ids?: number[];
  resolved_payment_method_ids?: number[];
}

export interface CoverageArea {
  id?: number;
  country: string;
  state?: string;
  postal_code?: number;
  cp_code31662?: string;
  locality?: string;
}

interface ShippingMethodsState {
  methods: ShippingMethod[];
  deletedMethods: ShippingMethod[];
  status: 'idle' | 'loading' | 'failed';
}

const initialState: ShippingMethodsState = {
  methods: [],
  deletedMethods: [],
  status: 'idle',
};

export const fetchShippingMethods = createAsyncThunk(
  'shippingMethods/list',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await conns<ShippingMethod[]>('eshop:dashboard:shipping:methods_list').get();
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to load methods: ${err}`);
    }
  }
);

export const fetchDeletedShippingMethods = createAsyncThunk(
  'shippingMethods/listDeleted',
  async (provider: string | undefined = undefined, { rejectWithValue }) => {
    try {
      const { data } = await conns<ShippingMethod[]>('eshop:dashboard:shipping:methods_list').get({
        deleted_only: true,
        ...(provider ? { provider } : {}),
      });
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to load deleted methods: ${err}`);
    }
  }
);

export const createShippingMethod = createAsyncThunk(
  'shippingMethods/create',
  async (payload: Omit<ShippingMethod, 'id'>, { rejectWithValue }) => {
    try {
      const { data } = await conns<ShippingMethod>('eshop:dashboard:shipping:methods_list').post(
        payload
      );
      return data;
    } catch (err) {
      return rejectWithValue(getShippingMethodApiError(err, 'No se pudo crear el metodo de envio.'));
    }
  }
);

export const updateShippingMethod = createAsyncThunk(
  'shippingMethods/update',
  async (payload: ShippingMethod, { rejectWithValue }) => {
    try {
      const { id, ...rest } = payload;
      const { data } = await conns<ShippingMethod>('eshop:dashboard:shipping:methods_list', [
        id,
      ]).put(rest);
      return data;
    } catch (err) {
      return rejectWithValue(getShippingMethodApiError(err, 'No se pudo actualizar el metodo de envio.'));
    }
  }
);

export const setShippingCoverage = createAsyncThunk(
  'shippingMethods/coverage',
  async (payload: { id: number; coverage: CoverageArea[] }, { rejectWithValue }) => {
    try {
      const { id, coverage } = payload;
      const { data } = await conns<ShippingMethod>('eshop:dashboard:shipping:methods_list', [
        id,
      ]).put({ coverage });
      return data;
    } catch (err) {
      return rejectWithValue(getShippingMethodApiError(err, 'No se pudo actualizar la cobertura.'));
    }
  }
);

export const deleteShippingMethod = createAsyncThunk(
  'shippingMethods/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await conns('eshop:dashboard:shipping:methods_list', [id]).delete();
      return id;
    } catch (err) {
      return rejectWithValue(`Failed to delete method: ${err}`);
    }
  }
);

const shippingMethodsSlice = createSlice({
  name: 'shippingMethods',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchShippingMethods.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchShippingMethods.fulfilled, (state, action) => {
        state.status = 'idle';
        state.methods = action.payload;
      })
      .addCase(fetchDeletedShippingMethods.fulfilled, (state, action) => {
        state.deletedMethods = action.payload;
      })
      .addCase(fetchShippingMethods.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(createShippingMethod.fulfilled, (state, action) => {
        state.methods.push(action.payload);
      })
      .addCase(updateShippingMethod.fulfilled, (state, action) => {
        state.deletedMethods = state.deletedMethods.filter((m) => m.id !== action.payload.id);
        if (action.payload.is_deleted) {
          state.methods = state.methods.filter((m) => m.id !== action.payload.id);
          state.deletedMethods.push(action.payload);
          return;
        }
        const idx = state.methods.findIndex((m) => m.id === action.payload.id);
        if (idx >= 0) state.methods[idx] = action.payload;
        else state.methods.push(action.payload);
      })
      .addCase(setShippingCoverage.fulfilled, (state, action) => {
        const idx = state.methods.findIndex((m) => m.id === action.payload.id);
        if (idx >= 0) state.methods[idx] = action.payload;
      })
      .addCase(deleteShippingMethod.fulfilled, (state, action) => {
        state.methods = state.methods.filter((m) => m.id !== action.payload);
      });
  },
});

export const selectShippingMethods = (s: RootState) => s?.shippingMethods?.methods ?? [];
export const selectDeletedShippingMethods = (s: RootState) => s?.shippingMethods?.deletedMethods ?? [];
export const selectShippingStatus = (s: RootState) => s?.shippingMethods?.status ?? 'idle';

export default shippingMethodsSlice.reducer;
